// src/pages/Auth/LoginLanding.tsx
/**
 * LoginLanding — Polished public landing + sign-in
 * -----------------------------------------------------------------------------
 * Brand: "TimberFlow by Canopy"
 * Visuals:
 *  - Clean hero with brand mark
 *  - Soft radial background + subtle grid pattern
 *  - Elevated login card with accent top-border
 *
 * Behavior:
 *  - Local "auth" demo flow (no server) using localStorage
 *  - Persists role to `role`
 *  - Redirects to `?next=/path` or "/" after login
 *  - Audit events for login/logout
 *
 * Storage keys:
 *  - auth:session:v1  => { user, role, loggedInAt, guest?: boolean }
 *  - auth:remember    => boolean
 *  - auth:lastUser    => string
 *  - role             => "Admin" | "Dispatcher" | "Loader" | "Trucker" | "Accountant"
 */

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { get, set, del } from "@/lib/storage";
import { useNotifications } from "@/lib/notifications";
import { logAudit } from "@/lib/audit";
import {
  LogIn,
  User,
  Lock,
  Shield,
  ChevronRight,
  ArrowRightCircle,
  Sparkles,
  ShieldCheck,
  Building2,
} from "lucide-react";

type Role = "Admin" | "Dispatcher" | "Loader" | "Trucker" | "Accountant";
const ROLES: Role[] = ["Admin", "Dispatcher", "Loader", "Trucker", "Accountant"];

type Session = { user: string; role: Role; loggedInAt: string; guest?: boolean };

const SESSION_KEY = "auth:session:v1";

export default function LoginLanding() {
  const nav = useNavigate();
  const loc = useLocation();
  const { push } = useNotifications();

  const qs = useMemo(() => new URLSearchParams(loc.search), [loc.search]);
  const next = qs.get("next") || "/";

  const existing = get<Session | null>(SESSION_KEY, null);

  // Form state
  const [user, setUser] = useState<string>(get("auth:lastUser", ""));
  const [pass, setPass] = useState<string>("");
  const [role, setRole] = useState<Role>(get<Role>("role", "Admin"));
  const [remember, setRemember] = useState<boolean>(!!get("auth:remember", true));
  const [showLogin, setShowLogin] = useState<boolean>(!existing);
  const [busy, setBusy] = useState<boolean>(false);

  useEffect(() => {
    // enforce square shape per design brief + light mode
    document.documentElement.setAttribute("data-shape", "square");
    document.documentElement.classList.remove("dark");
  }, []);

  // Submit on Enter
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Enter" && showLogin) {
        e.preventDefault();
        void doLogin(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showLogin, user, pass, role, remember]);

  const doLogin = async (guest = false) => {
    if (!guest && (!user || !pass)) {
      push({ title: "Enter email and password", level: "warning" });
      return;
    }
    setBusy(true);
    try {
      const finalUser = guest ? "guest" : user.trim();
      const chosenRole = role;
      const session: Session = {
        user: finalUser,
        role: chosenRole,
        loggedInAt: new Date().toISOString(),
        guest,
      };

      // Persist
      set(SESSION_KEY, session);
      set("role", chosenRole);
      set("auth:remember", remember);
      if (remember && !guest) set("auth:lastUser", finalUser);
      if (!remember) del("auth:lastUser");

      push({ title: guest ? "Continuing as guest" : "Welcome", body: finalUser });
      logAudit(
        { action: "auth:login", user: finalUser, role: chosenRole, method: guest ? "guest" : "password" },
        chosenRole
      );

      nav(next);
    } finally {
      setBusy(false);
    }
  };

  const doLogout = () => {
    const prev = get<Session | null>(SESSION_KEY, null);
    del(SESSION_KEY);
    push({ title: "Signed out" });
    logAudit({ action: "auth:logout", user: prev?.user, role: prev?.role }, prev?.role);
    setShowLogin(true);
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-white">
      {/* Radial brand glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full opacity-20"
        style={{
          background:
            "radial-gradient(closest-side, var(--brand-600), rgba(47,159,89,0))",
        }}
      />
      {/* Subtle grid pattern overlay */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            "linear-gradient(#0f172a0d 1px, transparent 1px), linear-gradient(90deg, #0f172a0d 1px, transparent 1px)",
          backgroundSize: "24px 24px",
        }}
      />

      {/* Top bar */}
      <div className="h-12 px-4 border-b bg-white/80 backdrop-blur supports-[backdrop-filter]:backdrop-blur flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-2">
          <div className="w-2.5 h-2.5 rounded-sm bg-[var(--brand-600)]" />
          <span className="font-semibold tracking-tight">TimberFlow by Canopy</span>
          <span className="ml-2 inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded bg-emerald-50 text-emerald-700">
            <ShieldCheck className="w-3.5 h-3.5" />
            Secure Access
          </span>
        </div>
        <div className="text-sm text-slate-500 hidden md:block">
          Need an account?{" "}
          <span className="underline decoration-dotted">Contact your admin</span>
        </div>
      </div>

      {/* Main */}
      <div className="max-w-6xl mx-auto px-4 py-10 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          {/* Hero copy */}
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-wider font-semibold text-[var(--brand-600)]">
              <Sparkles className="w-3.5 h-3.5" />
              Orchestrate your timber operations
            </div>
            <h1 className="mt-3 text-3xl md:text-5xl font-bold leading-tight">
              Plan jobs, track loads, and ship with confidence.
            </h1>
            <p className="mt-4 text-slate-600 text-lg">
              TimberFlow brings Sites, Jobs, Loads, Manifests, and Settlements together — with role-aware tools for the field and back office.
            </p>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <FeatureRow
                icon={<Building2 className="w-4 h-4" />}
                title="Sites & Jobs"
                text="Geofences, parcels, certifications, and rate history."
              />
              <FeatureRow
                icon={<Shield className="w-4 h-4" />}
                title="Manifests"
                text="Draft → Ready → Shipped → Delivered → Closed with exports."
              />
              <FeatureRow
                icon={<ChevronRight className="w-4 h-4" />}
                title="Loads Inbox"
                text="Validate, bulk actions, map to manifests seamlessly."
              />
              <FeatureRow
                icon={<ChevronRight className="w-4 h-4" />}
                title="Settlements"
                text="Totals, profit, commission overrides & print."
              />
            </div>
          </div>

          {/* Auth card */}
          <div className="relative">
            <div className="absolute -inset-0.5 rounded border border-emerald-200/60 pointer-events-none" />
            <div className="card shadow-sm">
              <div className="card-header flex items-center justify-between">
                <div className="font-semibold">Sign in</div>
                {existing && !showLogin ? (
                  <span className="text-xs text-slate-500">
                    You&apos;re signed in as{" "}
                    <span className="font-medium">{existing.user}</span>
                  </span>
                ) : null}
              </div>

              <div className="card-body">
                {existing && !showLogin ? (
                  <div className="space-y-3">
                    <div className="p-3 border rounded bg-slate-50">
                      <div className="text-sm">
                        Continue as <span className="font-medium">{existing.user}</span>{" "}
                        (<span className="uppercase">{existing.role}</span>)
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button className="btn" onClick={() => nav(next)} title="Go to app">
                        <ArrowRightCircle className="w-4 h-4 mr-2" />
                        Go to Dashboard
                      </button>
                      <button className="btn btn-secondary" onClick={doLogout}>
                        Switch Account
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Email */}
                    <label className="flex flex-col gap-1">
                      <span className="text-sm">Email</span>
                      <div className="relative">
                        <User className="absolute left-2 top-2.5 w-4 h-4 text-slate-400" />
                        <input
                          className="input pl-8"
                          type="email"
                          placeholder="you@company.com"
                          value={user}
                          onChange={(e) => setUser(e.target.value)}
                          autoFocus
                        />
                      </div>
                    </label>

                    {/* Password */}
                    <label className="flex flex-col gap-1 mt-2">
                      <span className="text-sm">Password</span>
                      <div className="relative">
                        <Lock className="absolute left-2 top-2.5 w-4 h-4 text-slate-400" />
                        <input
                          className="input pl-8"
                          type="password"
                          placeholder="••••••••"
                          value={pass}
                          onChange={(e) => setPass(e.target.value)}
                        />
                      </div>
                    </label>

                    {/* Role Segmented */}
                    <div className="mt-3">
                      <div className="text-sm mb-1">Role</div>
                      <div className="grid grid-cols-3 gap-2">
                        {ROLES.map((r) => (
                          <button
                            key={r}
                            type="button"
                            className={`btn-secondary text-sm ${role === r ? "ring-1 ring-[var(--brand-600)]" : ""}`}
                            onClick={() => setRole(r)}
                          >
                            {r}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Remember */}
                    <label className="mt-3 inline-flex items-center gap-2 select-none">
                      <input
                        type="checkbox"
                        className="accent-[var(--brand-600)]"
                        checked={remember}
                        onChange={(e) => setRemember(e.target.checked)}
                      />
                      <span className="text-sm">Remember me on this device</span>
                    </label>

                    {/* Actions */}
                    <div className="mt-4 flex items-center gap-2">
                      <button
                        className="btn"
                        onClick={() => doLogin(false)}
                        disabled={busy}
                        title="Sign in"
                      >
                        <LogIn className="w-4 h-4 mr-2" />
                        Sign in
                      </button>
                      <button
                        className="btn btn-secondary"
                        onClick={() => doLogin(true)}
                        disabled={busy}
                      >
                        Continue as Guest
                      </button>
                    </div>

                    {/* Help */}
                    <div className="mt-3 text-sm text-slate-500">
                      Forgot password?{" "}
                      <span className="underline decoration-dotted">
                        Ask your administrator
                      </span>
                    </div>

                    <div className="mt-6 text-xs text-slate-400">
                      By signing in you agree to the{" "}
                      <Link to="#" className="underline">
                        Terms
                      </Link>{" "}
                      and{" "}
                      <Link to="#" className="underline">
                        Privacy Policy
                      </Link>
                      .
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Accent shadow */}
            <div
              aria-hidden
              className="absolute -bottom-6 left-6 right-6 h-8 rounded-2xl blur-xl opacity-40"
              style={{
                background:
                  "radial-gradient(closest-side, rgba(47,159,89,.25), rgba(47,159,89,0))",
              }}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 text-xs text-slate-400">
          © {new Date().getFullYear()} <span className="font-medium">TimberFlow by Canopy</span>
        </div>
      </div>
    </div>
  );
}

/** Small feature row used in hero */
function FeatureRow({
  icon,
  title,
  text,
}: {
  icon: React.ReactNode;
  title: string;
  text: string;
}) {
  return (
    <div className="flex items-start gap-3 p-3 rounded border bg-white">
      <div className="mt-0.5 text-[var(--brand-600)]">{icon}</div>
      <div>
        <div className="font-medium">{title}</div>
        <div className="text-sm text-slate-600">{text}</div>
      </div>
    </div>
  );
}
